import type {
  SearchEngineAgent,
  SearchReasoningEvent,
} from "@shaarp/common";
import type {
  ChainOfThoughtState,
  ReasoningStepData,
  StepCompany,
  StepSource,
} from "./types";

function mergeStepStatus(
  a: ReasoningStepData["status"],
  b: ReasoningStepData["status"],
): ReasoningStepData["status"] {
  if (a === "active" || b === "active") return "active";
  if (a === b) return a;
  // One complete + one pending → the group is still in progress.
  return "active";
}

/**
 * Collapse consecutive steps sharing the same merge key into a single
 * aggregated step: sums `resultsCount` and concatenates sources, companies and
 * text lines. Steps with distinct keys (e.g. ScrapIO runs for different cities)
 * stay separate. Used to avoid repeating identical "contact search" steps.
 */
export function mergeAdjacentSteps(
  steps: readonly ReasoningStepData[],
  getKey: (step: ReasoningStepData) => string,
): ReasoningStepData[] {
  const merged: ReasoningStepData[] = [];
  let lastKey: string | undefined;

  for (const step of steps) {
    const key = getKey(step);
    const last = merged[merged.length - 1];

    if (last && key === lastKey) {
      last.sources = [...last.sources, ...step.sources];
      last.companies = [...last.companies, ...step.companies];
      last.textLines = [...last.textLines, ...step.textLines];
      last.agent = {
        ...last.agent,
        resultsCount: last.agent.resultsCount + step.agent.resultsCount,
      };
      last.status = mergeStepStatus(last.status, step.status);
    } else {
      merged.push({
        ...step,
        sources: [...step.sources],
        companies: [...step.companies],
        textLines: [...step.textLines],
        agent: { ...step.agent },
      });
      lastKey = key;
    }
  }

  return merged;
}

/** Find the step matching a given agentId, with cursor fallback for backward compat */
export function resolveStep(
  steps: ReasoningStepData[],
  agentId: string | undefined,
  cursorIndex: number,
): ReasoningStepData | undefined {
  if (agentId) {
    const match = steps.find((s) => s.agent.id === agentId);
    if (match) return match;
  }
  return cursorIndex >= 0 ? steps[cursorIndex] : undefined;
}

export function buildChainOfThoughtState(
  reasoningEvents: readonly SearchReasoningEvent[],
  searchEngineAgents: readonly SearchEngineAgent[],
  isStreaming: boolean,
): ChainOfThoughtState {
  const nonOrchestratorAgents = searchEngineAgents.filter(
    (a) => a.type !== "orchestrator",
  );

  const steps: ReasoningStepData[] = nonOrchestratorAgents.map(
    (agent, index) => ({
      id: agent.id ?? `${agent.type}-${index}`,
      agentType: agent.type,
      agent,
      status: "pending" as const,
      sources: [] as StepSource[],
      companies: [] as StepCompany[],
      textLines: [] as string[],
    }),
  );

  const orchestratorText: string[] = [];
  let conclusionText: string | null = null;
  let cursorIndex = -1;
  const activeAgentIds = new Set<string>();

  for (const event of reasoningEvents) {
    switch (event.type) {
      case "separator": {
        if (event.agentId) {
          activeAgentIds.add(event.agentId);
        }
        cursorIndex = Math.min(cursorIndex + 1, steps.length - 1);
        break;
      }

      case "thinking":
        orchestratorText.push(event.content);
        break;

      case "text":
        if (cursorIndex < 0) {
          orchestratorText.push(event.content);
        } else if (steps[cursorIndex]) {
          steps[cursorIndex].textLines.push(event.content);
        }
        break;

      case "source": {
        const sourceStep = resolveStep(steps, event.agentId, cursorIndex);
        if (sourceStep) {
          sourceStep.sources.push({ title: event.title, url: event.url });
        }
        break;
      }

      case "company": {
        const companyStep = resolveStep(steps, event.agentId, cursorIndex);
        if (companyStep) {
          companyStep.companies.push({ name: event.name, detail: event.detail });
        }
        break;
      }

      case "conclusion":
        conclusionText = event.content;
        break;
    }
  }

  if (isStreaming) {
    if (activeAgentIds.size > 0) {
      const lastActiveId = [...activeAgentIds].pop();
      for (const step of steps) {
        if (step.agent.id && activeAgentIds.has(step.agent.id)) {
          step.status = step.agent.id === lastActiveId ? "active" : "complete";
        } else {
          step.status = "pending";
        }
      }
    } else {
      for (let i = 0; i < steps.length; i++) {
        if (i < cursorIndex) {
          steps[i].status = "complete";
        } else if (i === cursorIndex) {
          steps[i].status = "active";
        } else {
          steps[i].status = "pending";
        }
      }
      if (cursorIndex < 0) {
        for (const step of steps) {
          step.status = "pending";
        }
      }
    }
  } else {
    for (const step of steps) {
      step.status = "complete";
    }
  }

  const showOrchestrator =
    orchestratorText.length > 0 || (isStreaming && cursorIndex < 0);

  return { orchestratorText, showOrchestrator, steps, conclusionText };
}
